import { useCallRecorder } from "@/features/calls/call-recorder-context";
import { api, readableApiError } from "@/shared/api";
import { Button, IconButton } from "@/shared/ui/primitives";
import { Bell, Download, Smartphone, X } from "lucide-react";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import {
  PushPreferenceError,
  applicationServerKey,
  discardPreviousAccount,
  forgetPushWorkspace,
  pushSupported,
  readPushPreferences,
  rememberPushWorkspace,
  requestPushPermission,
  savePushSubscription,
  subscriptionMatchesKey,
} from "./push-device";
import { activatePwaUpdate, installPwa, isIosDevice, pwaStore } from "./pwa-runtime";

type PushConfig = { enabled: boolean; publicKey: string | null };
type Props = { workspaceSlug: string; userId: string };

function usePushSettings({ workspaceSlug, userId }: Props) {
  const { registration, registrationFailed, standalone } = useSyncExternalStore(
    pwaStore.subscribe,
    pwaStore.getSnapshot,
  );
  const [config, setConfig] = useState<PushConfig | null>(null);
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);
  const [permission, setPermission] = useState<NotificationPermission>(() =>
    "Notification" in window ? Notification.permission : "default",
  );
  const [online, setOnline] = useState(navigator.onLine);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const supported = pushSupported();
  const iosNeedsInstall = isIosDevice() && !standalone;

  useEffect(() => {
    const connected = () => {
      setOnline(true);
      setRetry((value) => value + 1);
    };
    const disconnected = () => setOnline(false);
    const focused = () => {
      if ("Notification" in window) setPermission(Notification.permission);
      setRetry((value) => value + 1);
    };
    window.addEventListener("online", connected);
    window.addEventListener("offline", disconnected);
    window.addEventListener("focus", focused);
    return () => {
      window.removeEventListener("online", connected);
      window.removeEventListener("offline", disconnected);
      window.removeEventListener("focus", focused);
    };
  }, []);

  useEffect(() => {
    if (!registration || !supported || !online || iosNeedsInstall) return;
    if (retry > 0) setNotice(null);
    let cancelled = false;
    setBusy(true);
    setError(null);
    setConfig(null);
    setSubscription(null);
    void (async () => {
      // A previous account must stop receiving push before this one can opt in.
      const [nextConfig] = await Promise.all([
        api.push.config(workspaceSlug),
        discardPreviousAccount(userId, registration),
      ]);
      if (cancelled) return;
      setConfig(nextConfig);
      const preferences = readPushPreferences();
      const optedIn =
        preferences?.userId === userId && preferences.workspaces.includes(workspaceSlug);
      if (!optedIn || !nextConfig.enabled || !nextConfig.publicKey) return;
      const current = await registration.pushManager.getSubscription();
      if (cancelled) return;
      if (
        current &&
        Notification.permission === "granted" &&
        subscriptionMatchesKey(current, nextConfig.publicKey)
      ) {
        if (cancelled) return;
        await api.push.subscribe(workspaceSlug, current.toJSON());
        if (!cancelled) setSubscription(current);
      } else {
        // Rotation or a revoked browser permission needs a fresh explicit activation.
        if (current) {
          await api.push.revoke(workspaceSlug, current.endpoint);
          if (!(await current.unsubscribe())) throw new Error("Riprova a scollegare le notifiche.");
        }
        rememberPushWorkspace(userId, workspaceSlug, false);
      }
    })()
      .catch((failure: unknown) => {
        if (!cancelled)
          setError(readableApiError(failure, "Non riusciamo a verificare le notifiche. Riprova."));
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [registration, supported, online, iosNeedsInstall, userId, workspaceSlug, retry]);

  const enable = async () => {
    if (!registration || !config?.enabled || !config.publicKey || !online) return;
    let requested: Promise<NotificationPermission>;
    try {
      // Safari requires this call in the original click, before any network await.
      requested = requestPushPermission();
    } catch (failure) {
      setError(
        failure instanceof PushPreferenceError
          ? failure.message
          : "Consenti l'archiviazione nel browser e riprova.",
      );
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    let newlyCreated: PushSubscription | null = null;
    try {
      const nextPermission = await requested;
      setPermission(nextPermission);
      if (nextPermission !== "granted") return;
      await discardPreviousAccount(userId, registration);
      let current = await registration.pushManager.getSubscription();
      if (current && !subscriptionMatchesKey(current, config.publicKey)) {
        await api.push.revoke(workspaceSlug, current.endpoint);
        if (!(await current.unsubscribe()))
          throw new Error("Riprova a scollegare le notifiche precedenti.");
        current = null;
      }
      if (!current) {
        current = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey(config.publicKey),
        });
        newlyCreated = current;
      }
      await savePushSubscription(userId, workspaceSlug, current);
      setSubscription(current);
      setNotice("Notifiche attivate per questo spazio di lavoro su questo dispositivo.");
    } catch (failure) {
      if (newlyCreated) await newlyCreated.unsubscribe().catch(() => false);
      setError(
        failure instanceof PushPreferenceError
          ? failure.message
          : readableApiError(failure, "Non riusciamo ad attivare le notifiche. Riprova."),
      );
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    if (!subscription) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.push.revoke(workspaceSlug, subscription.endpoint);
      setSubscription(null);
      const storageAvailable = await forgetPushWorkspace(userId, workspaceSlug, subscription);
      if (!storageAvailable) {
        setError(
          "Notifiche disattivate su questo dispositivo. Il browser non permette di salvare le preferenze: riattivale negli altri spazi di lavoro se necessario.",
        );
      }
      setNotice("Notifiche disattivate per questo spazio di lavoro.");
    } catch (failure) {
      setError(readableApiError(failure, "Non riusciamo a disattivare le notifiche. Riprova."));
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    if (!subscription) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.push.test(workspaceSlug, subscription.endpoint);
      setNotice(
        "Notifica di prova inviata. Controlla anche le impostazioni di notifica del dispositivo.",
      );
    } catch (failure) {
      setError(readableApiError(failure, "Non riusciamo a inviare la prova. Riprova."));
    } finally {
      setBusy(false);
    }
  };

  const explanation = iosNeedsInstall
    ? "Su iPhone e iPad aggiungi prima Polaris alla schermata Home e aprila dall'icona. Le notifiche richiedono iOS o iPadOS 16.4 o successivi."
    : !supported
      ? "Questo browser non supporta le notifiche push. Prova da un browser aggiornato su una connessione HTTPS."
      : !online
        ? "Sei offline. Collegati a internet per gestire le notifiche."
        : registrationFailed
          ? "Non riusciamo a preparare l'app per le notifiche. Ricarica la pagina e riprova."
          : permission === "denied"
            ? "Le notifiche sono bloccate. Consenti le notifiche per Polaris nelle impostazioni del browser o del dispositivo, poi torna qui."
            : config && !config.enabled
              ? "Le notifiche push non sono ancora configurate su questo server. Contatta chi gestisce Polaris."
              : subscription
                ? "Attive per questo spazio di lavoro e questo account, solo su questo dispositivo. Arrivano per i nuovi lead, finché nessuno li segue, e per i messaggi dei lead che segui tu, con il nome o numero del contatto e l'anteprima del messaggio."
                : "Ricevi avvisi anche quando Polaris è chiusa: per i nuovi lead, finché nessuno li segue, e per i messaggi dei lead che segui tu, con il nome o numero del contatto e l'anteprima. Scegli tu se attivarli su questo dispositivo.";

  return {
    explanation,
    supported,
    iosNeedsInstall,
    registrationFailed,
    subscription,
    online,
    busy,
    permission,
    registration,
    config,
    error,
    notice,
    enable,
    disable,
    test,
    retry: () => setRetry((value) => value + 1),
  };
}

function PushSettings({ settings }: { settings: ReturnType<typeof usePushSettings> }) {
  const {
    explanation,
    supported,
    iosNeedsInstall,
    registrationFailed,
    subscription,
    online,
    busy,
    permission,
    registration,
    config,
    error,
    notice,
    enable,
    disable,
    test,
    retry,
  } = settings;
  return (
    <section className="pwa-section" aria-labelledby="push-settings-title">
      <h3 id="push-settings-title">
        <Bell size={19} aria-hidden="true" />
        Notifiche
      </h3>
      <p>{explanation}</p>
      {supported && !iosNeedsInstall && !registrationFailed ? (
        <div className="pwa-actions">
          {subscription ? (
            <>
              <Button
                variant="secondary"
                size="small"
                disabled={!online}
                busy={busy}
                onClick={() => void test()}
              >
                Invia una prova
              </Button>
              <Button
                variant="quiet"
                size="small"
                disabled={!online || busy}
                onClick={() => void disable()}
              >
                Disattiva
              </Button>
            </>
          ) : (
            <Button
              size="small"
              busy={busy}
              disabled={
                !online ||
                !registration ||
                !config?.enabled ||
                !config.publicKey ||
                permission === "denied"
              }
              onClick={() => void enable()}
            >
              Attiva notifiche
            </Button>
          )}
          {error ? (
            <Button variant="quiet" size="small" disabled={busy || !online} onClick={retry}>
              Riprova
            </Button>
          ) : null}
        </div>
      ) : null}
      {error ? (
        <p className="pwa-error" role="alert">
          {error}
        </p>
      ) : null}
      {notice ? <output className="pwa-notice">{notice}</output> : null}
    </section>
  );
}

export function AppSettings({
  workspaceSlug,
  userId,
  title,
}: Props & {
  /** The name on hover, when the collapsed sidebar shows the icon only. */
  title?: string;
}) {
  const [open, setOpen] = useState(false);
  const [installError, setInstallError] = useState<string | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const { installPrompt, standalone } = useSyncExternalStore(
    pwaStore.subscribe,
    pwaStore.getSnapshot,
  );
  const ios = isIosDevice();
  const push = usePushSettings({ workspaceSlug, userId });
  useEffect(() => {
    if (open) dialog.current?.showModal();
    else dialog.current?.close();
  }, [open]);
  return (
    <>
      <button
        className="nav-link pwa-settings-button"
        type="button"
        title={title}
        onClick={() => setOpen(true)}
      >
        <Smartphone aria-hidden="true" size={19} />
        <span className="nav-link__label">App e notifiche</span>
      </button>
      <dialog
        ref={dialog}
        className="modal pwa-dialog"
        aria-labelledby="app-settings-title"
        onClose={() => setOpen(false)}
        onCancel={() => setOpen(false)}
      >
        <header className="modal__header">
          <div>
            <h2 id="app-settings-title">App e notifiche</h2>
            <p>Le preferenze di questo dispositivo.</p>
          </div>
          <IconButton aria-label="Chiudi app e notifiche" onClick={() => setOpen(false)}>
            <X size={19} aria-hidden="true" />
          </IconButton>
        </header>
        {open ? (
          <div className="modal__body pwa-settings-body">
            <section className="pwa-section" aria-labelledby="install-settings-title">
              <h3 id="install-settings-title">
                <Download size={19} aria-hidden="true" />
                Polaris sul tuo dispositivo
              </h3>
              <p>
                {standalone
                  ? "Stai usando Polaris come app."
                  : "Apri Polaris dalla schermata Home o dal computer, in una finestra dedicata."}
              </p>
              {!standalone && installPrompt ? (
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => {
                    setInstallError(null);
                    void installPwa().catch(() =>
                      setInstallError("Installazione non riuscita. Riprova dal menu del browser."),
                    );
                  }}
                >
                  Installa Polaris
                </Button>
              ) : null}
              {!standalone && !installPrompt ? (
                <p className="pwa-hint">
                  {ios
                    ? "In Safari apri Condividi, scegli “Aggiungi alla schermata Home” e conferma. Poi apri Polaris dalla nuova icona."
                    : "Nel menu del browser cerca “Installa Polaris Leads” o “Installa app”. Se l'opzione non compare, puoi continuare a usare Polaris qui."}
                </p>
              ) : null}
              {installError ? (
                <p className="pwa-error" role="alert">
                  {installError}
                </p>
              ) : null}
            </section>
            <PushSettings settings={push} />
            <p className="pwa-hint">
              Lead e conversazioni richiedono una connessione internet. Nessun invio viene messo in
              coda quando sei offline.
            </p>
          </div>
        ) : null}
      </dialog>
    </>
  );
}

export function PwaUpdateBanner() {
  const { updateAvailable } = useSyncExternalStore(pwaStore.subscribe, pwaStore.getSnapshot);
  const { state } = useCallRecorder();
  const [confirm, setConfirm] = useState(false);
  const callBusy = state.phase !== "idle";
  if (!updateAvailable) return null;
  return (
    <section className="pwa-update" aria-label="Aggiornamento Polaris">
      <span>
        {callBusy
          ? "Un aggiornamento è pronto. Potrai applicarlo quando la chiamata sarà salvata."
          : confirm
            ? "Salva prima testi e modifiche aperte: l'app verrà ricaricata."
            : "È disponibile una nuova versione di Polaris."}
      </span>
      {!callBusy ? (
        <span className="pwa-actions">
          <Button
            size="small"
            variant="secondary"
            onClick={() => (confirm ? activatePwaUpdate() : setConfirm(true))}
          >
            {confirm ? "Ho salvato, aggiorna ora" : "Aggiorna"}
          </Button>
          {confirm ? (
            <Button size="small" variant="quiet" onClick={() => setConfirm(false)}>
              Più tardi
            </Button>
          ) : null}
        </span>
      ) : null}
    </section>
  );
}
